import {
  onActivityStreamDiffsSnapshot,
  onCanvasesSnapshot,
  onProjectLoadingProgressSnapshot,
  onProjectsSnapshot,
  onSubCanvasesSnapshot,
  setActivityStreamDiffs,
  setActivityStreamDiffsLoading,
  setCanvases,
  setCanvasesLoading,
  setElementMetadata,
  setElementMetadataLoading,
  setProjectLoadingProgress,
  setProjectLoadingProgressLoading,
  setProjects,
  setProjectsLoading,
  setSubCanvases,
  setSubCanvasesLoading,
  setProjectIdsByLastUpdated,
  setProjectIdsByLastUpdatedLoading,
  setCanvasPreviews,
  setFigmaContext,
  setDSPrompt,
  onDSPromptSnapshot,
  updateCanvasStoryboards,
} from "@/store/supabaseSlice";
import { DataKey, useSupabaseStore } from "@/store/zustand/supabaseStore";
import { Database, Json } from "@/utils/supabase.types";
import { Storyboard, Canvas } from "@/utils/types";
import {
  getUnexpiredToken,
  useActiveMainCanvas,
  useCanvasId,
  useUserId,
} from "@/utils/utils";
import { SupabaseClient, createClient } from "@supabase/supabase-js";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useSession, useUser } from "@clerk/clerk-react";
import useActivityStore from "@/store/zustand/activityStore";
import { useProjectCacheStore } from "@/store/zustand/projectHistoryStore";
import _ from "lodash";
import { MESSAGES_PER_CONVERSATION } from "@/store/zustand/supabaseStore";
import { CanvasPreview } from "@/utils/types";
import { track } from "@/utils/events/tracker";

interface SubscriptionOptions {
  single?: boolean;
  /**
   * If true, snapshots will be delivered via zustand.
   * If false, snapshots will be dispatched via Redux.
   */
  zustand?: boolean;

  /**
  * A *single* supabase filter to scope down events.
  * See: https://supabase.com/docs/guides/realtime/postgres-changes#available-filters
  * Track: https://github.com/supabase/realtime/issues/303
  * Track: https://github.com/supabase/realtime/issues/486
  *
  * This can only compare 1 value, so use the most specific one.

  * Allowed:
  * project_id=eq.${projectId}
  * canvas_id=lt.${canvasId}
  *
  * Not allowed:
  * project_id=eq.$(projectId),canvas_id=lt.${canvasId}
  * project_id=eq.$(projectId) or canvas_id=lt.${canvasId}
  */
  supabaseSingleFilter?: string;

  /**
   * A filter function used to reject payloads.
   * This should be used in conjunction with supabaseSingleFilter to
   * properly filter functions.
   *
   * Return true if a payload should be accepted.
   *
   * @param payload
   * @returns
   */
  payloadFilter?: (payload: any) => boolean;

  /**
   * Schema to use for the subscription. Defaults to "public".
   */
  schema?: string;
}

/**
 * Config for a generic db sync
 */
interface GenericDBConfig {
  [dbTableName: string]: {
    storeKey: DataKey;
    queryFilters?: {
      [key: string]: any;
    };
    query?: (supabase: SupabaseClient<Database>) => any;
    subscribeOptions: SubscriptionOptions;
    largeFields?: string[];
    requiresCanvasId: boolean;
  };
}

interface Props {
  children: any;
}

type SupabaseContextType = {
  triggerRefetchProjects?: () => void;
  triggerRefetchCanvasPreviews?: () => void;
  triggerRefetchElementMetadata?: () => void;
  triggerRefetchProjectLoadingProgress?: () => void;
};

const SupabaseContext = createContext<SupabaseContextType>({});

interface SideEffectProps {
  supabaseClient?: SupabaseClient<Database>;
  refetchProjectsTrigger: any;
  refetchCanvasPreviewsTrigger: any;
  refetchElementMetadataTrigger: any;
  refetchProjectLoadingProgressTrigger: any;
}

const DatabaseSyncSideEffect: React.FC<SideEffectProps> = React.memo(
  ({
    supabaseClient,
    refetchProjectsTrigger,
    refetchCanvasPreviewsTrigger,
    refetchElementMetadataTrigger,
    refetchProjectLoadingProgressTrigger,
  }) => {
    const canvasId = useCanvasId();

    const { user } = useUser();
    const dispatch = useDispatch<any>();

    const getProjectCanvasCache = useProjectCacheStore(
      (state) => state.getProjectCanvasCache
    );
    const setProjectCanvasCache = useProjectCacheStore(
      (state) => state.setProjectCanvasCache
    );

    const [searchParams, setSearchParams] = useSearchParams();

    const selectedProjectId = searchParams.get("project_id");

    const projectsLoadStatus = useSelector(
      (state: any) => state.supabaseStore.projectsLoadStatus
    );

    const projectIdsByLastUpdatedLoadingStatus = useSelector(
      (state: any) => state.supabaseStore.projectIdsByLastUpdatedLoadingStatus
    );

    const activityStreamList = useActivityStore(
      (state) => state.activityStreamList
    );
    const setActivityStreamLoading = useActivityStore(
      (state) => state.setActivityStreamLoading
    );
    const setActivities = useActivityStore((state) => state.setActivities);
    const onActivitySnapshot = useActivityStore(
      (state) => state.onActivitySnapshot
    );
    const refetchActivityStreamTrigger = useActivityStore(
      (state) => state.refetchActivityStreamTrigger
    );

    const activityStreamDiffs = useSelector(
      (state: any) => state.supabaseStore.activityStreamDiffs,
      shallowEqual
    );

    const canvasesLoadStatus = useSelector(
      (state: any) => state.supabaseStore.canvasesLoadStatus
    );
    const subCanvasesLoadStatus = useSelector(
      (state: any) => state.supabaseStore.subCanvasesLoadStatus
    );

    const elementMetadataLoadStatus = useSelector(
      (state: any) => state.supabaseStore.elementMetadataLoadStatus
    );

    const mainCanvas = useActiveMainCanvas();

    const projectId = mainCanvas?.project_id;

    const userId = useUserId();

    const [resubscribeTrigger, setResubscribeTrigger] = useState(null);

    /**
     * Only fetch, don't listen
     */
    const fetchSupabase = (
      dbTable: string,
      readQuery: any,
      setValue: (data: any) => void,
      onSnapshot: (
        payload: { newItems: any[]; deletedItems: any[] } | any
      ) => void,
      options: SubscriptionOptions = {}
    ) => {
      let { zustand, schema } = options;

      if (!schema) {
        schema = "public";
      }

      const fetchAllSupabaseRows = async () => {
        const { data, error } = await readQuery;

        if (error) {
          console.error("Error fetching data for table ", dbTable, error);
        }

        if (zustand) {
          setValue(data);
        } else {
          dispatch(setValue(data));
        }
      };

      fetchAllSupabaseRows();

      return () => {
        // No need to unsubscribe
      };
    };

    /**
     * Fetch an listen to a supabase DB table
     *
     */
    const fetchAndListenSupabase = (
      dbTable: string,
      readQuery: any,
      setValue: (data: any) => void,
      onSnapshot: (
        payload: { newItems: any[]; deletedItems: any[] } | any
      ) => void,

      options: SubscriptionOptions = {}
    ): (() => void) => {
      let { supabaseSingleFilter, single, zustand, schema, payloadFilter } =
        options;

      if (!schema) {
        schema = "public";
      }

      const fetchAllSupabaseRows = async () => {
        const { data, error } = await readQuery;

        if (error) {
          console.error("Error fetching data for table ", dbTable, error);
        }

        if (zustand) {
          setValue(data);
        } else {
          dispatch(setValue(data));
        }
      };

      fetchAllSupabaseRows();

      // @ts-expect-error TS18048
      const subscription = supabaseClient
        .channel(`channel-${dbTable}-${userId}`)
        .on(
          "postgres_changes",
          {
            event: "*",
            table: dbTable,
            schema: schema,
            filter: supabaseSingleFilter,
          },
          (payload) => {
            // TODO: This log shouldn't happen, remove if confirmed
            if (payload.table !== dbTable) {
              console.error(
                "Received payload for wrong table",
                payload.table,
                dbTable
              );
              return;
            }

            if (payloadFilter && !payloadFilter(payload)) {
              return;
            }

            if (zustand) {
              // No need to call dispatch on zustand
              if (single) {
                onSnapshot(payload.eventType === "DELETE" ? null : payload.new);
              } else {
                onSnapshot({
                  newItems: payload.eventType === "DELETE" ? [] : [payload.new],
                  deletedItems:
                    payload.eventType === "DELETE" ? [payload.old] : [],
                });
              }
            } else {
              if (single) {
                dispatch(
                  onSnapshot(
                    payload.eventType === "DELETE" ? null : payload.new
                  )
                );
              } else {
                dispatch(
                  onSnapshot({
                    newItems:
                      payload.eventType === "DELETE" ? [] : [payload.new],
                    deletedItems:
                      payload.eventType === "DELETE" ? [payload.old] : [],
                  })
                );
              }
            }
          }
        )
        .subscribe((callback, err) => {
          // Don't log CLOSED events in development
          if (
            callback !== "SUBSCRIBED" &&
            (import.meta.env.VITE_ENVIRONMENT === "PROD" ||
              callback !== "CLOSED")
          ) {
            console.debug(dbTable, callback, err);
          }

          // The resubscribe trigger is used to resubscribe if the connection is closed
          // However, we do not want an infinite resubscribe loop so we only trigger
          // if it hasn't been triggered in the last 5 seconds
          if (
            callback !== "SUBSCRIBED" &&
            (!resubscribeTrigger ||
              new Date().getTime() - resubscribeTrigger > 5000)
          ) {
            // @ts-expect-error TS2345
            setResubscribeTrigger(new Date().getTime());
          }
        });

      return () => {
        subscription?.unsubscribe();
      };
    };

    useEffect(() => {
      if (!userId || !supabaseClient) {
        return;
      }

      if (projectIdsByLastUpdatedLoadingStatus === "idle") {
        dispatch(setProjectIdsByLastUpdatedLoading());
      }

      const fetchProjectIdsByLastUpdated = async () => {
        const readQuery = supabaseClient.rpc(
          "get_project_ids_sorted_by_last_used",
          {
            env_inp: import.meta.env.VITE_ENVIRONMENT,
            auth_id: userId,
          }
        );

        const { data, error } = await readQuery;

        if (error) {
          console.error("Error fetching project ids by last updated", error);
          return;
        }

        dispatch(setProjectIdsByLastUpdated(data));
      };

      fetchProjectIdsByLastUpdated();
    }, [userId, supabaseClient, resubscribeTrigger, refetchProjectsTrigger]);

    // design system prompt
    useEffect(() => {
      if (!supabaseClient) return;

      const readQuery = supabaseClient
        .from("project_design_system_mapping")
        .select("prompt")
        .eq("project_id", projectId)
        .maybeSingle();

      return fetchSupabase(
        "project_design_system_mapping",
        readQuery,
        setDSPrompt,
        onDSPromptSnapshot
      );
    }, [supabaseClient, projectId, resubscribeTrigger]);

    /**
     * Fetch and listen to projects
     */
    useEffect(() => {
      if (!supabaseClient) {
        return;
      }

      if (projectsLoadStatus === "idle") {
        dispatch(setProjectsLoading());
      }

      const readQuery = supabaseClient.from("projects").select("*");

      return fetchSupabase(
        "projects",
        readQuery,
        setProjects,
        onProjectsSnapshot
      );
    }, [supabaseClient, resubscribeTrigger, refetchProjectsTrigger]);

    /**
     * Fetch and listen to canvas previews (canvases without storyboards)
     */
    useEffect(() => {
      if (!supabaseClient) {
        return;
      }

      if (selectedProjectId) {
        const cachedData = getProjectCanvasCache(selectedProjectId);
        if (
          cachedData?.canvasPreviews &&
          cachedData.canvasPreviews.length > 0
        ) {
          dispatch(setCanvasPreviews(cachedData.canvasPreviews));
        }
      }

      const fetchCanvasPreviews = async () => {
        if (!selectedProjectId) {
          return;
        }

        const readQuery = supabaseClient.rpc("get_canvas_previews_filtered", {
          project_id_inp: selectedProjectId,
          env_inp: import.meta.env.VITE_ENVIRONMENT,
        });

        const { data, error } = await readQuery;

        if (error) {
          console.error("Error fetching canvas previews", error);
          return;
        }

        dispatch(setCanvasPreviews(data));

        if (selectedProjectId && data) {
          const projectSpecificCanvases: { [canvasId: string]: CanvasPreview } =
            {};
          (data || []).forEach((canvas: CanvasPreview) => {
            projectSpecificCanvases[canvas.id] = canvas;
          });

          setProjectCanvasCache(selectedProjectId, data);
        }
        dispatch(setCanvasPreviews(data));
      };

      fetchCanvasPreviews();
    }, [
      supabaseClient,
      selectedProjectId,
      resubscribeTrigger,
      refetchCanvasPreviewsTrigger,
    ]);

    /**
     * Fetch and listen to canvases
     */
    useEffect(() => {
      if (!supabaseClient) {
        return;
      }

      if (!canvasId) return;

      if (canvasesLoadStatus === "idle") {
        dispatch(setCanvasesLoading());
      }

      let readQuery = supabaseClient
        .from("canvases")
        .select("*")
        .eq("env", import.meta.env.VITE_ENVIRONMENT)
        .eq("id", canvasId)
        .is("deleted_at", null);

      let options: SubscriptionOptions = {
        supabaseSingleFilter: `id=eq.${canvasId}`, // It's better to scope by this at Supabase level.
        payloadFilter: (payload) => {
          return payload.new.env === import.meta.env.VITE_ENVIRONMENT;
        },
      };

      return fetchSupabase(
        "canvases",
        readQuery,
        setCanvases,
        onCanvasesSnapshot,
        options
      );
    }, [supabaseClient, canvasId, resubscribeTrigger]);

    /**
     * Fetch and listen to subcanvases for a canvas
     */
    useEffect(() => {
      if (!supabaseClient || !canvasId) {
        return;
      }

      if (subCanvasesLoadStatus === "idle") {
        dispatch(setSubCanvasesLoading());
      }

      const readQuery = supabaseClient
        .from("sub_canvases")
        .select("*")
        .eq("canvas_id", canvasId);

      return fetchSupabase(
        "sub_canvases",
        readQuery,
        setSubCanvases,
        onSubCanvasesSnapshot,
        {
          supabaseSingleFilter: `canvas_id=eq.${canvasId}`,
        }
      );
    }, [supabaseClient, canvasId, resubscribeTrigger]);

    /**
     * Fetch element metadata
     */
    useEffect(() => {
      if (!supabaseClient || !projectId) {
        return;
      }

      if (elementMetadataLoadStatus === "idle") {
        dispatch(setElementMetadataLoading());
      }

      const fetchElementMetadata = async () => {
        const readQuery = supabaseClient
          .from("element_metadata")
          .select("*")
          .eq("project_id", projectId);

        const { data, error } = await readQuery;

        if (error) {
          console.error("Error fetching element metadata", error);
          return;
        }

        dispatch(setElementMetadata(data));
      };

      fetchElementMetadata();
    }, [supabaseClient, projectId, refetchElementMetadataTrigger]);

    // On May 14th a bug was introduced where an excessive number of storyboards
    // were precreated during streaming, which then resulted in successive
    // storyboard showing up really far off to the side and the canvas would be
    // so zoomed out it would look empty.
    // This is a hack to clean these up, and should be removed once its been
    // long enough
    const [cleanedCanvases, setCleanedCanvases] = useState(new Set<string>());
    useEffect(() => {
      if (!supabaseClient || !canvasId || cleanedCanvases.has(canvasId)) {
        return;
      }

      const cleanupCanvasStoryboards = async () => {
        try {
          // Get the current canvas data
          const { data: canvasData, error } = await supabaseClient
            .from("canvases")
            .select("*")
            .eq("id", canvasId)
            .eq("env", import.meta.env.VITE_ENVIRONMENT)
            .is("deleted_at", null)
            .single();

          if (error || !canvasData?.storyboards) {
            // Mark as processed even if no data to avoid retrying
            setCleanedCanvases((prev) => new Set(prev).add(canvasId));
            return;
          }

          // Convert to proper Canvas type (storyboards from DB are Json type)
          const canvas: Canvas = {
            ...canvasData,
            storyboards: canvasData.storyboards as Storyboard[],
            annotations: (canvasData.annotations as any) || [],
          };

          // Check if cleanup is needed
          const cleanupResult = cleanupStoryboards(canvas.storyboards);

          if (cleanupResult.needsPersistence) {
            track("AI_CHAT_WARNING", {
              warning: "Cleaning up canvas storyboards",
              data: {
                canvasId,
                removedCount: cleanupResult.removedCount,
                shiftedCount: cleanupResult.shiftedCount,
              },
            });
            // Update the database with cleaned storyboards
            await updateCanvasStoryboards(
              supabaseClient,
              dispatch,
              cleanupResult.cleanedStoryboards,
              canvas
            );
          }

          // Mark this canvas as processed
          setCleanedCanvases((prev) => new Set(prev).add(canvasId));
        } catch (error) {
          console.error(`Failed to cleanup canvas ${canvasId}:`, error);
          // Still mark as processed to avoid infinite retries
          setCleanedCanvases((prev) => new Set(prev).add(canvasId));
        }
      };

      // Run cleanup when canvas is first opened
      cleanupCanvasStoryboards();
    }, [supabaseClient, canvasId, cleanedCanvases, dispatch]);

    /**
     * Fetch and listen to the activity stream items & events
     */
    useEffect(() => {
      if (!canvasId || !supabaseClient) {
        return;
      }

      // Activities
      setActivityStreamLoading();

      const readQuery = supabaseClient
        .from("activity_stream")
        .select("*")
        .eq("canvas_id", canvasId)
        .order("timestamp", { ascending: false });

      const activitiesUnsubscribe = fetchSupabase(
        "activity_stream",
        readQuery,
        setActivities,
        onActivitySnapshot,
        {
          supabaseSingleFilter: `canvas_id=eq.${canvasId}`,
          zustand: true,
        }
      );

      return () => {
        activitiesUnsubscribe();
      };
    }, [
      supabaseClient,
      canvasId,
      resubscribeTrigger,
      refetchActivityStreamTrigger,
    ]);

    useEffect(() => {
      if (!canvasId || !supabaseClient) {
        return;
      }

      // Diffs

      dispatch(setActivityStreamDiffsLoading());

      // Only fetch diffs for activities that we do not have diffs for
      const activitiesWithoutDiffs = activityStreamList.filter(
        (activity) => !activityStreamDiffs[activity.id]
      );

      // don't fetch if we have the diffs for all the activities
      if (activitiesWithoutDiffs.length === 0) {
        return;
      }

      const diffReadQuery = supabaseClient
        .from("activity_stream_diffs")
        .select("*")
        .eq("canvas_id", canvasId)
        .in(
          "activity_id",
          activitiesWithoutDiffs.map((a) => a.id)
        );

      const activityDiffsUnsubscribe = fetchSupabase(
        "activity_stream_diffs",
        diffReadQuery,
        setActivityStreamDiffs,
        onActivityStreamDiffsSnapshot,
        {
          supabaseSingleFilter: `canvas_id=eq.${canvasId}`,
        }
      );

      return () => {
        activityDiffsUnsubscribe();
      };
    }, [supabaseClient, canvasId, resubscribeTrigger, activityStreamList]);

    /**
     * Fetch and listen to the loading progress
     */
    useEffect(() => {
      if (!canvasId || !supabaseClient) {
        return;
      }

      dispatch(setProjectLoadingProgressLoading());

      const readQuery = supabaseClient
        .from("project_loading_progress")
        .select("*")
        .eq("canvas_id", canvasId)
        .single();

      return fetchSupabase(
        "project_loading_progress",
        readQuery,
        setProjectLoadingProgress,
        onProjectLoadingProgressSnapshot,
        {
          supabaseSingleFilter: `canvas_id=eq.${canvasId}`,
          single: true,
        }
      );
    }, [
      supabaseClient,
      canvasId,
      resubscribeTrigger,
      refetchProjectLoadingProgressTrigger,
    ]);

    /**
     * All generic supabase store fetching and listening into the zustand state
     */
    useEffect(() => {
      if (!supabaseClient) {
        return;
      }

      const DB_TABLES_TO_SYNC: GenericDBConfig = {
        feature_prd: {
          storeKey: "featurePrds",
          queryFilters: canvasId ? { canvas_id: canvasId } : undefined,
          subscribeOptions: {
            single: false,
            supabaseSingleFilter: canvasId
              ? `canvas_id=eq.${canvasId}`
              : undefined,
          },
          largeFields: ["prd", "canvas_visual_context", "design_suggestions"],
          requiresCanvasId: false,
        },
        ai_chat_conversations: {
          storeKey: "aiChatConversations",
          queryFilters: {
            canvas_id: canvasId,
          },
          subscribeOptions: {
            single: false,
            supabaseSingleFilter: `canvas_id=eq.${canvasId}`,
          },
          requiresCanvasId: true,
        },
        ai_chat_messages: {
          storeKey: "aiChatMessages",
          query: (supabase) => {
            return supabase.rpc("fetch_latest_conversation_messages", {
              canvas_id: canvasId,
              messages_per_conversation: MESSAGES_PER_CONVERSATION,
            });
          },
          subscribeOptions: {
            single: false,
            supabaseSingleFilter: `canvas_id=eq.${canvasId}`,
            schema: "public",
          },
          largeFields: ["message_context"],
          requiresCanvasId: true,
        },
        saved_storyboard_html: {
          storeKey: "savedStoryboardHtml",
          queryFilters: {
            canvas_id: canvasId,
          },
          subscribeOptions: {
            single: false,
            supabaseSingleFilter: `canvas_id=eq.${canvasId}`,
            schema: "public",
          },
          largeFields: ["html"],
          requiresCanvasId: true,
        },
      };

      const sbStoreState = useSupabaseStore.getState();

      const allUnsubscribes = Object.keys(DB_TABLES_TO_SYNC).map(
        (dbTable: any) => {
          const config = DB_TABLES_TO_SYNC[dbTable];
          const storeKey: DataKey = config.storeKey;

          if (config.requiresCanvasId && !canvasId) {
            // Skipping
            return () => {};
          }

          const loadingStatus =
            useSupabaseStore.getState()[storeKey + "Status"];
          if (!loadingStatus) {
            throw Error(
              "DB Store not configured correctly, could not find field " +
                storeKey +
                "Status"
            );
          }

          if (loadingStatus === "idle") {
            sbStoreState.setLoading(storeKey);
          }

          // Create the read query and filter
          let readQuery: any;

          if (config.query) {
            readQuery = config.query(supabaseClient);
          } else {
            readQuery = supabaseClient.from(dbTable).select("*");
            Object.keys(config.queryFilters || {}).forEach((filterKey) => {
              // @ts-expect-error TS18048
              const filterValue = config.queryFilters[filterKey];
              readQuery = readQuery.eq(filterKey, filterValue);
            });
          }

          config.subscribeOptions["zustand"] = true;

          return fetchSupabase(
            dbTable,
            readQuery,
            (data) => sbStoreState.setAllValues(storeKey, data),
            (payload) =>
              sbStoreState.onDbSnapshot(storeKey, payload, config.largeFields),
            config.subscribeOptions
          );
        }
      );

      return () => {
        allUnsubscribes.forEach((unsubscribe) => unsubscribe());
      };
    }, [supabaseClient, canvasId, resubscribeTrigger]);

    return <></>;
  }
);

const DatabaseSyncWrapper: React.FC<Props> = React.memo((props) => {
  const { user } = useUser();
  const canvasId = useCanvasId();
  const dispatch = useDispatch();
  const { session } = useSession();
  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  // Create the supabase client which can then be used elsewhere using the provider
  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
  const setSupabaseClient = useSupabaseStore(
    (state) => state.setSupabaseClient
  );

  const [refetchProjectsTrigger, setRefetchProjectsTrigger] = useState(0);
  const triggerRefetchProjects = () => {
    setRefetchProjectsTrigger((prev) => prev + 1);
  };

  const [refetchCanvasPreviewsTrigger, setRefetchCanvasPreviewsTrigger] =
    useState(0);
  const triggerRefetchCanvasPreviews = () => {
    setRefetchCanvasPreviewsTrigger((prev) => prev + 1);
  };

  const [refetchElementMetadataTrigger, setRefetchElementMetadataTrigger] =
    useState(0);
  const triggerRefetchElementMetadata = () => {
    setRefetchElementMetadataTrigger((prev) => prev + 1);
  };

  const [
    refetchProjectLoadingProgressTrigger,
    setRefetchProjectLoadingProgressTrigger,
  ] = useState(0);
  const triggerRefetchProjectLoadingProgress = () => {
    setRefetchProjectLoadingProgressTrigger((prev) => prev + 1);
  };

  useEffect(() => {
    if (!user) {
      console.log("User is not set");
      return;
    }

    if (!session) {
      console.log("Session is not set");
      return;
    }

    if (supabaseClient) {
      return;
    }

    console.log("Initializing supabase client");

    const getAccessToken = async () => {
      if (!sessionRef.current) {
        return null;
      }

      const token = await getUnexpiredToken(sessionRef.current);
      return token || null;
    };

    const supabase: SupabaseClient = createClient(
      import.meta.env.VITE_BACKEND_SUPABASE_URL || "",
      import.meta.env.VITE_BACKEND_SUPABASE_ANON || "",
      {
        accessToken: getAccessToken,
        global: {},
        realtime: {
          // Set based on this github issue: https://github.com/supabase/supabase-js/issues/1390
          accessToken: getAccessToken,
          heartbeatIntervalMs: 5000,
          reconnectAfterMs: () => {
            return 5000;
          },
        },
      }
    );

    setSupabaseClient(supabase);
  }, [Boolean(session), Boolean(user)]);

  useEffect(() => {
    if (!supabaseClient) {
      return;
    }

    // setup channel for figma contexts
    const figmaContextsChannel = supabaseClient.channel(
      `figma_contexts_${canvasId}`
    );

    figmaContextsChannel
      .on("broadcast", { event: "contextAdded" }, async (payload) => {
        if (payload.payload?.canvas_id) {
          const { data, error } = await supabaseClient
            .from("figma_ai_contexts")
            .select("*")
            .eq("canvas_id", payload.payload.canvas_id)
            .maybeSingle();

          if (error) {
            console.error("Error fetching figma context", error);
          }

          if (data) {
            dispatch(setFigmaContext(data));
          }
        }
      })
      .subscribe((status) => {
        // console.log("Subscription status:", status);
      });

    // clean up on unmount
    return () => {
      supabaseClient.removeChannel(figmaContextsChannel);
    };
  }, [supabaseClient]);

  return (
    <>
      <SupabaseContext.Provider
        value={{
          triggerRefetchProjects,
          triggerRefetchCanvasPreviews,
          triggerRefetchElementMetadata,
          triggerRefetchProjectLoadingProgress,
        }}
      >
        {props.children}
      </SupabaseContext.Provider>
      <DatabaseSyncSideEffect
        supabaseClient={supabaseClient}
        refetchProjectsTrigger={refetchProjectsTrigger}
        refetchCanvasPreviewsTrigger={refetchCanvasPreviewsTrigger}
        refetchElementMetadataTrigger={refetchElementMetadataTrigger}
        refetchProjectLoadingProgressTrigger={
          refetchProjectLoadingProgressTrigger
        }
      />
    </>
  );
});

export const useSupabase = () => {
  const context = useContext(SupabaseContext);
  if (!context) {
    throw new Error("useSupabase must be used within a DatabaseSyncWrapper");
  }
  return context;
};

export default DatabaseSyncWrapper;

/**
 * Cleanup function to:
 * 1. Remove ALL pre-created storyboards (`created === false`).
 * 2. Shift the valid storyboards whose `created_at` is between **May 13 and May 27 2025**.
 *
 * In order to clean up canvases that experienced the bug where many many
 * storyboards were precreated incorrectly and were causing successive
 * storyboard creations to appear way too far on the right
 *
 * TODO: Remove this code after a while, when we feel good that most people that
 * would've been affected have already hit this cleanup
 */
export const cleanupStoryboards = (
  storyboards: Storyboard[]
): {
  cleanedStoryboards: Storyboard[];
  needsPersistence: boolean;
  removedCount: number;
  shiftedCount: number;
} => {
  if (!Array.isArray(storyboards)) {
    return {
      cleanedStoryboards: storyboards,
      needsPersistence: false,
      removedCount: 0,
      shiftedCount: 0,
    };
  }

  const parseCreatedDate = (sb: Storyboard): Date | null =>
    sb.created_at && typeof sb.created_at === "string"
      ? new Date(sb.created_at)
      : null;

  const removedInvalidStoryboards = storyboards.filter(
    (sb) => sb.created === false
  );
  const removedCount = removedInvalidStoryboards.length;
  const validStoryboards = storyboards.filter(
    (sb) => !removedInvalidStoryboards.includes(sb)
  );

  if (removedCount === 0) {
    return {
      cleanedStoryboards: storyboards,
      needsPersistence: false,
      removedCount: 0,
      shiftedCount: 0,
    };
  }

  const CANVAS_BUFFER_X = 24;

  const invalidSbLeft = Math.min(
    ...removedInvalidStoryboards.map((sb) =>
      typeof sb.x === "number" ? sb.x : 0
    )
  );
  const validSbLeftOfInvalid = validStoryboards.filter(
    (sb) => (typeof sb.x === "number" ? sb.x : 0) < invalidSbLeft
  );
  const rightEdgeBeforeInvalid = Math.max(
    0,
    ...validSbLeftOfInvalid.map(
      (sb) => (typeof sb.x === "number" ? sb.x : 0) + (sb.width || 0)
    )
  );
  let currentRightEdge =
    rightEdgeBeforeInvalid + (rightEdgeBeforeInvalid > 0 ? CANVAS_BUFFER_X : 0);

  // If its a valid storyboard created during the bug period, it should be shifted left
  const shouldShiftLeft = (sb: Storyboard): boolean => {
    const createdDate = parseCreatedDate(sb);
    const may13 = new Date("2025-05-13T00:00:00Z");
    const may27End = new Date("2025-05-27T23:59:59Z");
    return (
      createdDate !== null && createdDate >= may13 && createdDate <= may27End
    );
  };

  const validStoryboardsSortedX = [...validStoryboards].sort(
    (a, b) => (a.x || 0) - (b.x || 0)
  );

  let shiftedCount = 0;
  const idToUpdated: Map<string, Storyboard> = new Map();
  validStoryboardsSortedX.forEach((sb) => {
    const currentX = typeof sb.x === "number" ? sb.x : 0;

    // Wasn't affected by the bug
    if (currentX < invalidSbLeft) {
      idToUpdated.set(sb.id, sb);
      return;
    }

    if (shouldShiftLeft(sb)) {
      // Move it over to the right edge
      if (currentX !== currentRightEdge) {
        shiftedCount++;
      }
      idToUpdated.set(sb.id, { ...sb, x: currentRightEdge });
      currentRightEdge += (sb.width || 0) + CANVAS_BUFFER_X;
    } else {
      // Not shifted, maintain its original position; ensure cursor keeps up
      idToUpdated.set(sb.id, sb);
      currentRightEdge = Math.max(
        currentRightEdge,
        currentX + (sb.width || 0) + CANVAS_BUFFER_X
      );
    }
  });

  // Build the cleaned storyboard list in the original order, excluding invalid ones
  const cleanedStoryboards: Storyboard[] = storyboards
    .filter((sb) => !removedInvalidStoryboards.includes(sb))
    .map((sb) => idToUpdated.get(sb.id) || sb);

  return {
    cleanedStoryboards,
    needsPersistence: removedCount > 0 || shiftedCount > 0,
    removedCount,
    shiftedCount,
  };
};
